import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
  Radio,
  FormControlLabel,
  Checkbox,
  TextField,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import * as services from "./../../../services/saleng";
import moment from "moment";
import "moment/locale/th";

export default function POSelectionDialog({
  open,
  onClose,
  transaction,
  poSelect = [],
  lockSelect = [],
  onSubmit,
}) {
  const [selectedRadio, setSelectedRadio] = useState("none"); // "none" | "po" | "lock"
  const [checkedItems, setCheckedItems] = useState([]); // ids ของ PO
  const [lockCheckedItems, setLockCheckedItems] = useState([]); // ids ของ lock
  const [lockStartDate, setLockStartDate] = useState("");
  const [lockEndDate, setLockEndDate] = useState("");

  // fetch ภายใน dialog
  const [poItems, setPoItems] = useState([]);
  const [lockItems, setLockItems] = useState([]);
  const [loadingPO, setLoadingPO] = useState(false);
  const [loadingLock, setLoadingLock] = useState(false);

  const maxSelect = 10;

  // ------------------------
  // ดึงข้อมูลจาก API ภายใน dialog
  // ------------------------
  const fetchPOs = useCallback(async () => {
    try {
      const customerId = transaction?.customerId;
      if (!customerId) {
        setPoItems([]);
        return;
      }
      setLoadingPO(true);
      const resData = await services.getPurchaseOrders({
        customerId,
        currentDate: new Date().toISOString().split("T")[0],
      });
      const openedPOs = resData.filter((po) => po.status !== "close");
      setPoItems(openedPOs);
    } catch (error) {
      console.error("เกิดข้อผิดพลาดในการดึง PO:", error);
      setPoItems([]);
    } finally {
      setLoadingPO(false);
    }
  }, [transaction?.customerId]);

  const fetchLockPricing = useCallback(async () => {
    try {
      const customerId = transaction?.customerId;
      if (!customerId) {
        setLockItems([]);
        return;
      }
      setLoadingLock(true);
      const res = await services.getLockPricingByCustomer(customerId);
      setLockItems(res?.data || []);
    } catch (error) {
      console.error("เกิดข้อผิดพลาดในการดึง lock-pricing:", error);
      setLockItems([]);
    } finally {
      setLoadingLock(false);
    }
  }, [transaction?.customerId]);

  // เปิด dialog แล้วค่อยโหลด (และ reload เมื่อ transaction เปลี่ยน)
  useEffect(() => {
    if (!open) return;
    fetchPOs();
    fetchLockPricing();
  }, [fetchLockPricing, fetchPOs, open, transaction]);

  // ตั้ง default วันที่ช่วง lock: start = min(createdAt), end = today
  useEffect(() => {
    if (!open || lockItems.length === 0) return;

    const itemsWithCreated = lockItems.filter((x) => !!x?.createdAt);
    if (itemsWithCreated.length === 0) {
      const today = new Date().toISOString().split("T")[0];
      setLockStartDate((prev) => prev || today);
      setLockEndDate((prev) => prev || today);
      return;
    }

    const minCreatedAt = itemsWithCreated.reduce((min, item) => {
      const d = new Date(item.createdAt);
      return d < min ? d : min;
    }, new Date(itemsWithCreated[0].createdAt));

    const startStr = minCreatedAt.toISOString().split("T")[0];
    const endStr = new Date().toISOString().split("T")[0];

    setLockStartDate((prev) => (prev ? prev : startStr));
    setLockEndDate((prev) => (prev ? prev : endStr));
  }, [open, lockItems]);

  // sync ค่าเริ่มต้นจาก parent (preselect)
  useEffect(() => {
    if (!open) return;

    if (poSelect && poSelect.length > 0) {
      const ids = (poSelect ?? [])
        .map((po) => po.id || po.purchaseOrderId)
        .filter(Boolean);
      setCheckedItems(ids);
      setSelectedRadio("po");
    } else if (lockSelect && lockSelect.length > 0) {
      const ids = lockSelect.map((l) => l.id);
      setLockCheckedItems(ids);
      setSelectedRadio("lock");
    } else {
      setCheckedItems([]);
      setLockCheckedItems([]);
      setSelectedRadio("none");
    }
  }, [open, poSelect, lockSelect]);

  // Auto select PO ตาม poSelect เมื่อโหลด PO เสร็จ
  useEffect(() => {
    if (!open || poItems.length === 0 || !(poSelect?.length > 0)) return;

    const autoCheckedIds = poItems
      .filter((po) =>
        poSelect.some((s) => (s.id || s.purchaseOrderId) === po.id),
      )
      .map((po) => po.id);

    if (autoCheckedIds.length > 0) {
      setCheckedItems(autoCheckedIds);
      setSelectedRadio("po");
    }
  }, [open, poItems, poSelect]);

  // ------------------------
  // Handlers
  // ------------------------
  const handleToggleCheck = (id, type) => {
    if (type === "po") {
      setCheckedItems((prev) => {
        if (prev.includes(id)) return prev.filter((v) => v !== id);
        if (prev.length < maxSelect) return [...prev, id];
        return prev;
      });
    } else if (type === "lock") {
      setLockCheckedItems((prev) => {
        if (prev.includes(id)) return prev.filter((v) => v !== id);
        return [...prev, id]; // lock ไม่มี limit
      });
    }
  };

  const handleSelectAll = (type, rows) => {
    const ids = rows.map((r) => Number(r.id));
    if (type === "po") setCheckedItems(ids);
    else setLockCheckedItems(ids);
  };

  // ------------------------
  // PO columns/rows
  // ------------------------
  const poColumns = [
    {
      field: "checkbox",
      headerName: "",
      width: 50,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: () => (
        <Checkbox
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          checked={checkedItems.length === poRows.length && poRows.length > 0}
          indeterminate={
            checkedItems.length > 0 && checkedItems.length < poRows.length
          }
          onChange={(e) =>
            e.target.checked
              ? handleSelectAll("po", poRows)
              : setCheckedItems([])
          }
        />
      ),
      renderCell: (params) => (
        <Checkbox
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          checked={checkedItems.includes(params.row.id)}
          onChange={() => handleToggleCheck(params.row.id, "po")}
        />
      ),
    },
    { field: "poNumber", headerName: "PO #", width: 200 },
    { field: "detail", headerName: "รายละเอียด", flex: 1 },
    { field: "quantity", headerName: "จำนวนสินค้า", width: 130 },
    { field: "startDate", headerName: "เริ่มต้น", width: 120 },
    { field: "endDate", headerName: "สิ้นสุด", width: 120 },
  ];

  const poRows = useMemo(
    () =>
      (poItems || []).map((t) => {
        const shortPo = t.poNumber?.split("-").pop()?.padStart(3, "0") ?? "-";

        return {
          id: t.id,
          poNumber: shortPo,
          detail: t.detail || "-",
          quantity: `${t.items?.length ?? 0} ไอเท็ม`,
          startDate: t.startDate
            ? moment(t.startDate).add(543, "year").format("D MMM YYYY")
            : "-",
          endDate: t.endDate
            ? moment(t.endDate).add(543, "year").format("D MMM YYYY")
            : "-",
        };
      }),
    [poItems],
  );

  // ------------------------
  // LOCK columns/rows
  // ------------------------
  const lockColumns = [
    {
      field: "checkbox",
      headerName: "",
      width: 50,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: () => (
        <Checkbox
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          checked={
            lockCheckedItems.length === filteredLockRows.length &&
            filteredLockRows.length > 0
          }
          indeterminate={
            lockCheckedItems.length > 0 &&
            lockCheckedItems.length < filteredLockRows.length
          }
          onChange={(e) =>
            e.target.checked
              ? handleSelectAll("lock", filteredLockRows)
              : setLockCheckedItems([])
          }
        />
      ),
      renderCell: (params) => (
        <Checkbox
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          checked={lockCheckedItems.includes(params.row.id)}
          onChange={() => handleToggleCheck(params.row.id, "lock")}
        />
      ),
    },
    { field: "productName", headerName: "สินค้า", flex: 1 },
    { field: "lockWeight", headerName: "น้ำหนักล็อก", width: 120 },
    { field: "actualWeight", headerName: "น้ำหนักจริง", width: 120 },
    { field: "purchasePrice", headerName: "ราคา/หน่วย", width: 120 },
    { field: "predictPrice", headerName: "ราคาทำนาย", width: 120 },
    { field: "status", headerName: "สถานะ", width: 100 },
  ];

  const filteredLockRows = useMemo(() => {
    const hasRange = lockStartDate && lockEndDate;
    return (lockItems || [])
      .filter((t) => {
        if (!hasRange) return false;
        const startOk = t.startDate
          ? new Date(t.startDate) >= new Date(lockStartDate)
          : true;
        const endOk = t.endDate
          ? new Date(t.endDate) <= new Date(lockEndDate)
          : true;
        return startOk && endOk;
      })
      .map((t) => ({
        id: t.id,
        productName: t.transactionItem?.productName || t.product?.title || "-",
        lockWeight: t.lockWeight ?? 0,
        actualWeight: t.actualWeight ?? 0,
        purchasePrice: t.purchasePrice ?? 0,
        predictPrice: t.predictPrice ?? 0,
        status: t.status || "-",
      }));
  }, [lockItems, lockStartDate, lockEndDate]);

  // ------------------------
  // Submit
  // ------------------------
  const handleSubmit = () => {
    if (selectedRadio === "po") {
      const selectedPOs = (poItems || []).filter((po) =>
        checkedItems.includes(po.id),
      );
      onSubmit?.({
        type: "po",
        po: selectedPOs,
        lock: [],
        dateRange: null,
      });
      onClose?.();
      return;
    }
    if (selectedRadio === "lock") {
      const selectedLocks = (lockItems || []).filter((lk) =>
        lockCheckedItems.includes(lk.id),
      );
      onSubmit?.({
        type: "lock",
        po: [],
        lock: selectedLocks,
        dateRange: {
          start: lockStartDate || null,
          end: lockEndDate || null,
        },
      });
      onClose?.();
      return;
    }
    // none
    onSubmit?.({
      type: "none",
      po: [],
      lock: [],
      dateRange: null,
    });
    onClose?.();
  };

  return (
    <Dialog 
      open={open} 
      onClose={() => onClose?.()} 
      fullWidth 
      maxWidth="lg"
      PaperProps={{
        style: { borderRadius: "24px" },
        className: "shadow-2xl overflow-hidden bg-slate-50"
      }}
    >
      {/* Header */}
      <div className="bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between sticky top-0 z-10">
         <div>
            <h2 className="text-xl font-black text-slate-800">การเลือกราคาสินค้า</h2>
            <p className="text-sm text-slate-400 font-medium mt-0.5">
                เลือกประเภทราคาที่ต้องการใช้กับตั๋วนี้
            </p>
         </div>
      </div>

      <DialogContent className="!p-6 bg-slate-50">
        <div className="flex flex-col gap-4">
            
            {/* Option 1: PO */}
            <div 
                onClick={() => {
                    setCheckedItems([]);
                    setLockCheckedItems([]);
                    setSelectedRadio("po");
                }}
                className={`bg-white rounded-2xl p-5 border-2 cursor-pointer transition-all shadow-sm relative overflow-hidden group ${selectedRadio === 'po' ? 'border-blue-500 ring-4 ring-blue-50' : 'border-transparent hover:border-gray-200'}`}
            >
                <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-3">
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${selectedRadio === 'po' ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`}>
                            {selectedRadio === 'po' && <div className="w-2.5 h-2.5 rounded-full bg-white" />}
                        </div>
                        <span className={`text-lg font-bold ${selectedRadio === 'po' ? 'text-slate-800' : 'text-slate-600'}`}>
                            ใช้ใบราคาสินค้า (PO)
                        </span>
                    </div>
                    <span className="bg-blue-50 text-blue-600 text-xs font-bold px-3 py-1 rounded-full border border-blue-100">
                        {checkedItems.length}/{maxSelect} รายการ
                    </span>
                </div>

                {loadingPO ? (
                    <div className="text-center py-4 text-slate-400 text-sm">กำลังโหลดข้อมูล...</div>
                ) : (
                    <div className={`mt-3 transition-all ${selectedRadio === 'po' ? 'opacity-100' : 'opacity-50 grayscale'}`}>
                        <div className="bg-slate-50 rounded-xl border border-gray-100 overflow-hidden">
                             <DataGrid
                                rows={poRows}
                                columns={poColumns}
                                autoHeight
                                disableRowSelectionOnClick
                                hideFooter
                                loading={loadingPO}
                                sx={{
                                    border: 'none',
                                    '& .MuiDataGrid-columnHeaders': {
                                        backgroundColor: '#f8fafc',
                                        color: '#475569',
                                        fontWeight: 'bold',
                                        fontSize: '0.9rem',
                                    },
                                    '& .MuiDataGrid-cell': {
                                        borderBottom: '1px solid #f1f5f9',
                                        color: '#334155',
                                        fontSize: '0.9rem',
                                    },
                                    '& .MuiDataGrid-row:hover': {
                                        backgroundColor: '#f1f5f9',
                                    },
                                }}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Option 2: Lock Price */}
            <div 
                onClick={() => {
                    setCheckedItems([]);
                    setLockCheckedItems([]);
                    setSelectedRadio("lock");
                }}
                className={`bg-white rounded-2xl p-5 border-2 cursor-pointer transition-all shadow-sm relative overflow-hidden group ${selectedRadio === 'lock' ? 'border-purple-500 ring-4 ring-purple-50' : 'border-transparent hover:border-gray-200'}`}
            >
                <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-3">
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${selectedRadio === 'lock' ? 'border-purple-500 bg-purple-500' : 'border-gray-300'}`}>
                            {selectedRadio === 'lock' && <div className="w-2.5 h-2.5 rounded-full bg-white" />}
                        </div>
                        <span className={`text-lg font-bold ${selectedRadio === 'lock' ? 'text-slate-800' : 'text-slate-600'}`}>
                            สินค้าล็อกราคา
                        </span>
                    </div>
                    <span className="bg-purple-50 text-purple-600 text-xs font-bold px-3 py-1 rounded-full border border-purple-100">
                        {lockCheckedItems.length} รายการ
                    </span>
                </div>
                
                {selectedRadio === "lock" && (
                    <div className="mt-4 animate-fade-in">
                        <div className="flex items-center gap-3 mb-4 bg-purple-50/50 p-3 rounded-xl border border-purple-100">
                             <div className="flex items-center gap-2">
                                <span className="text-sm font-bold text-slate-500">ช่วงเวลา:</span>
                                <input 
                                    type="date" 
                                    value={lockStartDate}
                                    onChange={(e) => setLockStartDate(e.target.value)}
                                    className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-purple-200"
                                />
                                <span className="text-slate-400 font-bold">-</span>
                                <input 
                                    type="date" 
                                    value={lockEndDate}
                                    onChange={(e) => setLockEndDate(e.target.value)}
                                    className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-purple-200"
                                />
                             </div>
                        </div>

                        {lockStartDate && lockEndDate && (
                            <div className="bg-slate-50 rounded-xl border border-gray-100 overflow-hidden">
                                <DataGrid
                                    rows={filteredLockRows}
                                    columns={lockColumns}
                                    autoHeight
                                    disableRowSelectionOnClick
                                    hideFooter
                                    loading={loadingLock}
                                    sx={{
                                        border: 'none',
                                        '& .MuiDataGrid-columnHeaders': {
                                            backgroundColor: '#f8fafc',
                                            color: '#475569',
                                            fontWeight: 'bold',
                                            fontSize: '0.9rem',
                                        },
                                        '& .MuiDataGrid-cell': {
                                            borderBottom: '1px solid #f1f5f9',
                                            color: '#334155',
                                            fontSize: '0.9rem',
                                        },
                                        '& .MuiDataGrid-row:hover': {
                                            backgroundColor: '#f1f5f9',
                                        },
                                    }}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Option 3: None */}
            <div 
                onClick={() => {
                    setCheckedItems([]);
                    setLockCheckedItems([]);
                    setSelectedRadio("none");
                }}
                className={`bg-white rounded-2xl p-5 border-2 cursor-pointer transition-all shadow-sm relative overflow-hidden group ${selectedRadio === 'none' ? 'border-slate-500 ring-4 ring-slate-100' : 'border-transparent hover:border-gray-200'}`}
            >
                <div className="flex items-center gap-3">
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${selectedRadio === 'none' ? 'border-slate-500 bg-slate-500' : 'border-gray-300'}`}>
                        {selectedRadio === 'none' && <div className="w-2.5 h-2.5 rounded-full bg-white" />}
                    </div>
                    <span className={`text-lg font-bold ${selectedRadio === 'none' ? 'text-slate-800' : 'text-slate-600'}`}>
                        ไม่ใช้การล็อกราคา (ทั่วไป)
                    </span>
                 </div>
            </div>

        </div>
      </DialogContent>

      <div className="p-6 bg-white border-t border-gray-100 flex gap-3 justify-end">
        <button
          onClick={() => onClose?.()}
          className="px-6 py-3 rounded-xl text-slate-500 font-bold hover:bg-slate-50 transition-colors"
        >
          ยกเลิก
        </button>
        <button
          onClick={handleSubmit}
          className="px-8 py-3 bg-slate-800 hover:bg-slate-900 text-white rounded-xl font-bold shadow-lg shadow-slate-200 transition-all transform active:scale-95"
        >
          ยืนยันการเลือก
        </button>
      </div>
    </Dialog>
  );
}
